import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

export interface MyOrganization {
  id: string;
  name: string;
  slug: string;
  planType: string;
  createdAt: string;
  _count: { members: number; projects: number };
  myRole: string;
  joinedAt: string;
}

export async function getMyOrganizations(): Promise<MyOrganization[]> {
  const res = await api.get<ApiResponse<MyOrganization[]>>('/organizations');
  return res.data.data ?? [];
}

export interface CreatedOrganization {
  id: string;
  name: string;
  slug: string;
  planType: string;
}

export async function createOrganization(data: { name: string; slug: string }): Promise<CreatedOrganization> {
  const res = await api.post<ApiResponse<CreatedOrganization>>('/organizations', data);
  return res.data.data!;
}

// CHG-135 REQ-OMS-001: 멤버십 상태 (User.deletedAt 의 계정 비활성화와는 별개)
export type OrgMemberStatus = 'ACTIVE' | 'PENDING' | 'INACTIVE' | 'SUSPENDED';

export interface OrgMember {
  id: string;
  userId: string;
  role: string;
  joinedAt: string;
  isDeactivated: boolean;
  // CHG-135 REQ-OMS-007: 멤버 관리 페이지 표시용
  status: OrgMemberStatus;
  statusChangedAt: string | null;
  // CHG-133 Step 7
  user: {
    id: string;
    email: string;
    name: string;
    avatarUrl: string | null;
    role: string;
    createdAt: string;
    displayColor?: string | null;
    nickname?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  };
}

export async function getOrgMembers(orgId: string): Promise<OrgMember[]> {
  const res = await api.get<ApiResponse<OrgMember[]>>(`/organizations/${orgId}/members`);
  return res.data.data ?? [];
}

export async function removeOrgMember(orgId: string, memberId: string): Promise<void> {
  await api.delete(`/organizations/${orgId}/members/${memberId}`);
}

// CHG-135 REQ-OMS-008: 멤버 상태 변경
export interface UpdatedMemberStatus {
  organizationId: string;
  userId: string;
  status: OrgMemberStatus;
  statusChangedAt: string;
  statusChangedBy: {
    id: string;
    name: string;
    avatarUrl: string | null;
    displayColor: string | null;
    nickname: string | null;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

export async function updateOrgMemberStatus(
  orgId: string,
  userId: string,
  status: OrgMemberStatus,
  reason?: string,
): Promise<UpdatedMemberStatus> {
  const res = await api.put<ApiResponse<UpdatedMemberStatus>>(
    `/organizations/${orgId}/members/${userId}/status`,
    { status, ...(reason ? { reason } : {}) },
  );
  return res.data.data!;
}

export async function updateOrgMemberRole(orgId: string, memberId: string, role: string): Promise<void> {
  await api.patch(`/organizations/${orgId}/members/${memberId}`, { role });
}

export async function checkSlug(slug: string): Promise<boolean> {
  const res = await api.get<ApiResponse<{ available: boolean }>>(`/organizations/check-slug/${slug}`);
  return res.data.data?.available ?? false;
}

export async function checkOrgName(name: string, excludeOrgId?: string): Promise<boolean> {
  const params = excludeOrgId ? `?excludeOrgId=${excludeOrgId}` : '';
  const res = await api.get<ApiResponse<{ available: boolean }>>(
    `/organizations/check-name/${encodeURIComponent(name.trim())}${params}`,
  );
  return res.data.data?.available ?? false;
}

export async function updateOrganization(orgId: string, data: { name?: string }): Promise<void> {
  await api.patch(`/organizations/${orgId}`, data);
}
